import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
const SKIP_CART_NOTIFICATION_FLAG = 'skipCartNotification';
const PRIMARY_COLOR_OPTION_NAMES = new Set(['color1', 'colour1']);
const SECONDARY_COLOR_OPTION_NAMES = new Set(['color2', 'colour2']);
const GENERIC_COLOR_OPTION_NAMES = new Set(['color', 'colour']);
const ALL_COLOR_OPTION_NAMES = new Set([
  ...PRIMARY_COLOR_OPTION_NAMES,
  ...SECONDARY_COLOR_OPTION_NAMES,
  ...GENERIC_COLOR_OPTION_NAMES,
]);

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLButtonElement} payButton - Sticky bar's pay button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  static DESKTOP_DEFAULT_BOTTOM = 36;
  static MOBILE_FOOTER_DOCK_OFFSET = 50;
  static INLINE_BUY_BUTTONS_BREAKPOINT = 1024;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {IntersectionObserver | null} */
  #productFormObserver = null;

  /** @type {HTMLElement | null} */
  #sourceSectionElement = null;

  /** @type {HTMLElement | null} */
  #footerElement = null;

  /** @type {boolean} */
  #sourceFormVisible = true;

  /** @type {boolean} */
  #legacyStickyEnabled = true;

  connectedCallback() {
    super.connectedCallback();

    this.#applyLegacyMainSettings();
    if (!this.#legacyStickyEnabled) return;

    this.#applyStickyVisibilityMode();
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;

    const { signal } = this.#abortController;
    const target = this.#getSourceSectionElement();
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    window.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
    window.addEventListener('resize', this.#handleScroll, { signal, passive: true });

    this.#getInitialQuantity();
    this.#handleScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectProductFormObserver();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #applyLegacyMainSettings() {
    const legacyConfig = this.#getLegacyConfigElement();
    if (!(legacyConfig instanceof HTMLElement)) return;

    const legacyEnabled = legacyConfig.dataset.enabled;
    if (legacyEnabled === 'false') {
      this.hidden = true;
      this.#legacyStickyEnabled = false;
      return;
    }

    this.#legacyStickyEnabled = true;

    const legacyPosition = legacyConfig.dataset.position;
    if (!legacyPosition) return;

    this.refs.stickyBar.classList.remove(
      'sticky-add-to-cart__bar--left',
      'sticky-add-to-cart__bar--center',
      'sticky-add-to-cart__bar--right'
    );
    this.refs.stickyBar.classList.add(`sticky-add-to-cart__bar--${legacyPosition}`);
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    const flyToCartElement = this.#createFlyToCartElement(cartIcon);

    await onAnimationEnd(
      flyToCartElement ? [this.refs.addToCartButton, flyToCartElement] : this.refs.addToCartButton
    );

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles the pay button click - adds to cart and redirects to Shop Pay checkout
   */
  handlePayClick = async () => {
    if (!this.#targetAddToCartButton) return;

    document.documentElement.dataset[SKIP_CART_NOTIFICATION_FLAG] = 'true';

    // Set up a one-time listener for successful cart add
    const handleCartSuccess = () => {
      // Redirect to checkout
      window.location.href = '/checkout?payment=shop_pay';
    };

    const handleCartError = () => {
      delete document.documentElement.dataset[SKIP_CART_NOTIFICATION_FLAG];
    };

    // Add the event listener
    document.addEventListener(ThemeEvents.cartUpdate, handleCartSuccess, { once: true });
    document.addEventListener(ThemeEvents.cartError, handleCartError, { once: true });

    // Trigger the add to cart
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility logic for current breakpoint after morphing
    this.#applyStickyVisibilityMode();
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptionEntries = [
      ...Array.from(variantPicker.querySelectorAll('input:checked')).map((option) =>
        this.#getSelectedOptionEntry(/** @type {HTMLInputElement} */ (option))
      ),
      ...Array.from(variantPicker.querySelectorAll('select')).map((option) =>
        this.#getSelectedOptionEntry(/** @type {HTMLSelectElement} */ (option))
      ),
    ];

    const hasColorOption = selectedOptionEntries.some((entry) => ALL_COLOR_OPTION_NAMES.has(entry.name));
    const visibleOptionEntries = selectedOptionEntries.filter((entry) => entry.label !== '');

    const preferredOption =
      visibleOptionEntries.find((entry) => entry.name === 'lensname') ||
      visibleOptionEntries.find((entry) => PRIMARY_COLOR_OPTION_NAMES.has(entry.name)) ||
      visibleOptionEntries.find((entry) => SECONDARY_COLOR_OPTION_NAMES.has(entry.name)) ||
      visibleOptionEntries.find((entry) => GENERIC_COLOR_OPTION_NAMES.has(entry.name)) ||
      (!hasColorOption ? visibleOptionEntries[0] : undefined);

    if (!preferredOption?.label) {
      variantTitleElement.textContent = '';
      return;
    }

    variantTitleElement.textContent = preferredOption.label;
  };

  /**
   * Creates a fly-to-cart element when the theme setting enables the animation.
   * @param {Element | null} cartIcon
   * @returns {FlyToCart | null}
   */
  #createFlyToCartElement(cartIcon) {
    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (!animationEnabled || !cartIcon || !this.refs.productImage) return null;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    return flyToCartElement;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} field
   * @returns {{ name: string, label: string }}
   */
  #getSelectedOptionEntry(field) {
    const optionName = this.#getSelectedOptionName(field);

    if (field instanceof HTMLInputElement) {
      const label = field.closest('label');
      const labelText = label?.textContent?.replace(/\s+/g, ' ').trim();
      // Liquid writes the strict display label into `data-option-display-label` so sticky fallback
      // stays aligned with summary surfaces even when the visible picker text uses a raw fallback.
      const hasDisplayLabel = field.dataset.optionDisplayLabel !== undefined;
      const displayLabel = field.dataset.optionDisplayLabel?.trim() || '';

      return {
        name: optionName,
        label: hasDisplayLabel ? displayLabel : (labelText || field.value.trim()),
      };
    }

    const selectedOption = field.options[field.selectedIndex];
    const labelText = selectedOption?.textContent?.replace(/\s+/g, ' ').trim();
    const hasDisplayLabel = selectedOption?.dataset.optionDisplayLabel !== undefined;
    const displayLabel = selectedOption?.dataset.optionDisplayLabel?.trim() || '';

    return {
      name: optionName,
      label: hasDisplayLabel ? displayLabel : (labelText || field.value.trim()),
    };
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} field
   * @returns {string}
   */
  #getSelectedOptionName(field) {
    if (field instanceof HTMLInputElement) {
      const fieldset = field.closest('fieldset');
      const genericClasses = new Set([
        'variant-option--buttons',
        'variant-option--swatches',
        'variant-option--small',
        'variant-option--medium',
        'variant-option--large',
        'variant-option--images',
      ]);

      const optionClass = Array.from(fieldset?.classList || []).find(
        (className) => className.startsWith('variant-option--') && !genericClasses.has(className)
      );

      return optionClass ? optionClass.replace('variant-option--', '') : '';
    }

    const optionNameMatch = field.name.match(/^options\[(.+)\]$/);

    return optionNameMatch
      ? optionNameMatch[1].toLowerCase().replace(/[\s_-]+/g, '')
      : '';
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #applyStickyVisibilityMode() {
    this.#observeProductForm();
    this.#handleScroll();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleProductFormIntersection = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    this.#sourceFormVisible = entry.isIntersecting;
    this.#updateStickyState();
  };

  #observeProductForm() {
    this.#disconnectProductFormObserver();

    const formTarget = this.#getProductFormButtons() ?? this.#getProductForm();
    if (!formTarget) {
      this.#sourceFormVisible = false;
      this.#updateStickyState();
      return;
    }

    this.#productFormObserver = new IntersectionObserver(this.#handleProductFormIntersection, {
      threshold: 0,
    });
    this.#productFormObserver.observe(formTarget);

    const records = this.#productFormObserver.takeRecords();
    if (records.length > 0) {
      this.#handleProductFormIntersection(records);
    } else {
      this.#sourceFormVisible = true;
      this.#updateStickyState();
    }
  }

  #disconnectProductFormObserver() {
    this.#productFormObserver?.disconnect();
    this.#productFormObserver = null;
  }

  #handleScroll = () => {
    this.#updateTopState();
    this.#updateStickyState();
  };

  #updateTopState() {
    if (this.#isDesktopDockViewport()) {
      delete this.dataset.atTop;
      return;
    }

    if (window.scrollY === 0 || window.pageYOffset === 0) {
      this.dataset.atTop = 'true';
      return;
    }

    delete this.dataset.atTop;
  }

  #updateStickyState() {
    const nextState = this.#getNextStickyState();

    if (nextState === 'hidden') {
      this.#clearStickyState();
      return;
    }

    this.#setStickyState(nextState);
  }

  /** @returns {'hidden' | 'floating' | 'docked'} */
  #getNextStickyState() {
    if (!this.#hasInlineBuyButtonsViewport()) {
      return this.#hasReachedMobileFooterDockZone() ? 'docked' : 'floating';
    }

    if (this.#sourceFormVisible) {
      return 'hidden';
    }

    const reachedDockZone = this.#isDesktopDockViewport()
      ? this.#hasReachedDockZone()
      : this.#hasReachedMobileFooterDockZone();

    return reachedDockZone ? 'docked' : 'floating';
  }

  /** @param {'hidden' | 'floating' | 'docked'} state */
  #setStickyState(state) {
    if (this.refs.stickyBar.dataset.state === state) {
      return;
    }

    this.refs.stickyBar.dataset.state = state;
  }

  #clearStickyState() {
    if (this.refs.stickyBar.dataset.state !== 'hidden') {
      this.refs.stickyBar.dataset.state = 'hidden';
    }
  }

  #hasReachedDockZone() {
    const barHeight = this.refs.stickyBar.offsetHeight;
    if (barHeight <= 0) return false;

    const dockRootRect = this.getBoundingClientRect();
    const dockTriggerTop =
      window.innerHeight - StickyAddToCartComponent.DESKTOP_DEFAULT_BOTTOM - barHeight;

    return dockRootRect.top <= dockTriggerTop;
  }

  #hasReachedMobileFooterDockZone() {
    const footerElement = this.#getFooterElement();
    if (!footerElement) return false;

    const footerRect = footerElement.getBoundingClientRect();

    return footerRect.top <= window.innerHeight + StickyAddToCartComponent.MOBILE_FOOTER_DOCK_OFFSET;
  }

  #isDesktopDockViewport() {
    return window.innerWidth >= 1200;
  }

  #hasInlineBuyButtonsViewport() {
    return window.innerWidth >= StickyAddToCartComponent.INLINE_BUY_BUTTONS_BREAKPOINT;
  }

  /** @returns {HTMLElement | null} */
  #getFooterElement() {
    if (this.#footerElement?.isConnected) return this.#footerElement;

    this.#footerElement = document.querySelector('body > footer');

    return this.#footerElement instanceof HTMLElement ? this.#footerElement : null;
  }

  /** @returns {HTMLElement | null} */
  #getSourceSectionElement() {
    if (this.#sourceSectionElement?.isConnected) return this.#sourceSectionElement;

    const sourceSectionId = this.dataset.sourceSectionId?.trim();

    if (sourceSectionId) {
      this.#sourceSectionElement = document.querySelector(`#shopify-section-${sourceSectionId}`);
    }

    if (!(this.#sourceSectionElement instanceof HTMLElement)) {
      this.#sourceSectionElement = this.#getProductForm()?.closest('.shopify-section') ?? null;
    }

    return this.#sourceSectionElement instanceof HTMLElement ? this.#sourceSectionElement : null;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sourceSectionId = this.dataset.sourceSectionId?.trim();

    if (sourceSectionId) {
      const scopedProductForm = document.querySelector(
        `#shopify-section-${sourceSectionId} product-form-component[data-product-id="${productId}"]`
      );

      if (scopedProductForm instanceof HTMLElement) {
        return scopedProductForm;
      }
    }

    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Gets the inline buy buttons container when present.
   * @returns {HTMLElement | null}
   */
  #getProductFormButtons() {
    return this.#getProductForm()?.querySelector('[ref="productFormButtons"]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getLegacyConfigElement() {
    return this.#getSourceSectionElement()?.querySelector('[data-sticky-add-to-cart-legacy-config]') ?? null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
